import { useEffect, useRef, useState } from 'react';
import { ImageUp, Pencil, Trash2 } from 'lucide-react';
import { Avatar, type AvatarColor } from './ui';
import { uploadAttachment } from '../../lib/attachments';
import { useTranslation } from '../../lib/i18n';
import { useGenericBi } from '../../lib/use-generic-bi';
import { Spinner } from '../Spinner';
import styles from './AvatarPhotoEditor.module.css';

/**
 * An {@link Avatar} that can set or clear its photo — the shared self-photo / contact-photo control.
 *
 * At rest it is the plain circle. Hovered (or focused), the artwork takes a 60% ink scrim with the
 * 16px pencil centred on it, and it becomes a button that opens the image options: upload a new photo,
 * or remove the one that's there. This is web's idiom for native's editable-avatar → confirmation
 * dialog (Take photo / Choose from library / Remove photo); one file input covers both native pick
 * paths, since a mobile browser's file picker already offers the camera.
 *
 * Modelled on {@link WorkspaceBadgeEditor}: the upload is the ordinary attachment path (`/api/upload-url`
 * → direct WixMP PUT, `uploadAttachment`), so a phone photo gets the same downscale/compress treatment
 * as a chat image before it becomes the durable `static.wixstatic.com` URL handed to `onSave`. `onSave('')`
 * is the clear. Unlike the workspace editor there is no name-field blur to coordinate, so this is simpler.
 */
export function AvatarPhotoEditor({
  initials,
  photoUrl,
  onPhotoError,
  size,
  color,
  canRemove,
  onSave,
  onError,
  page,
  buttonName,
  className,
}: {
  initials: string;
  photoUrl?: string | null;
  onPhotoError?: () => void;
  size: number;
  color?: AvatarColor;
  /** Offer the Remove item — pass false when there is nothing to clear. */
  canRemove: boolean;
  /** Persist the chosen photo URL, or `''` to clear. Throwing surfaces the shared image error. */
  onSave: (url: string) => Promise<void>;
  /** Raises an image-update failure (or null to clear it) so the host can show it. */
  onError?: (message: string | null) => void;
  /** BI page_name for the edit trigger click. */
  page: string;
  /** BI button_name for the edit trigger click. */
  buttonName: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Dismiss on outside pointerdown / Escape — the same shape the composer's AttachMenu uses.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = () => {
    onError?.(null);
    setOpen((v) => !v);
    logPageClickBi({ page_name: page, button_name: buttonName });
  };

  const save = async (url: string): Promise<void> => {
    try {
      await onSave(url);
      onError?.(null);
    } catch {
      onError?.(t('workspace.imageError'));
    }
  };

  const onPicked = async (file: File | undefined) => {
    if (!file) return;
    onError?.(null);
    setUploading(true);
    try {
      const result = await uploadAttachment(file, { imageOnly: true });
      if (!result.ok) {
        onError?.(result.message || t('workspace.imageError'));
        return;
      }
      await save(result.attachment.url);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div ref={rootRef} className={[styles.root, className].filter(Boolean).join(' ')}>
      <button
        type="button"
        className={styles.trigger}
        onClick={toggle}
        disabled={uploading}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('workspace.editImage')}
        title={t('workspace.editImage')}
      >
        <Avatar initials={initials} photoUrl={photoUrl} onPhotoError={onPhotoError} size={size} color={color} />
        <span className={styles.editLayer} aria-hidden>
          {uploading ? <Spinner /> : <Pencil size={16} />}
        </span>
      </button>

      {open && (
        <div role="menu" className={styles.menu}>
          <button
            type="button"
            role="menuitem"
            className={styles.item}
            onClick={() => {
              setOpen(false);
              fileRef.current?.click();
            }}
          >
            <span className={styles.itemIcon} aria-hidden>
              <ImageUp size={16} />
            </span>
            <span>{t('workspace.uploadPhoto')}</span>
          </button>
          {canRemove && (
            <button
              type="button"
              role="menuitem"
              className={styles.item}
              onClick={() => {
                setOpen(false);
                void save('');
              }}
            >
              <span className={styles.itemIcon} aria-hidden>
                <Trash2 size={16} />
              </span>
              <span>{t('workspace.removePhoto')}</span>
            </button>
          )}
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className={styles.fileInput}
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Reset first, so picking the SAME file again still fires a change event.
          e.target.value = '';
          void onPicked(file);
        }}
      />
    </div>
  );
}
